import { mergeMap, of } from "rxjs";
import { Injectable } from '@angular/core';
import { Action, State, Store, type StateContext } from '@ngxs/store';
import { NodeControlActions } from '@medsurf/flat-actions';
import { ApiControlService } from '@medsurf/flat-services';
import { AuthControlState, NavigationControlState, NodeEntityState } from '../../internal';
import { Navigate } from '@ngxs/router-plugin';

export interface NodeControlStateModel {
  rootNodesLoaded: boolean;
}

@State<NodeControlStateModel>({
  name: 'nodeControl',
  defaults: {
    rootNodesLoaded: false,
  }
})
@Injectable()
export class NodeControlState {
  constructor(public store: Store,
              public apiControlService: ApiControlService) {
  }

  //<editor-fold desc="Actions">
  
  @Action(NodeControlActions.GetRootNodesSuccess)
  getRootNodesSuccess({patchState}: StateContext<NodeControlStateModel>) {
    patchState({
      rootNodesLoaded: true
    });
  }

  @Action(NodeControlActions.GetNodeById)
  getNodeById(_: StateContext<NodeControlStateModel>,
              {id}: NodeControlActions.GetNodeById) {
    if (id === null) {
      return;
    } else {
      const node = this.store.selectSnapshot(NodeEntityState.nodeById$(id));
      if (node) {
        return;
      }
    }
    return this.apiControlService.sendMessage(
      new NodeControlActions.GetNodeByIdRequest(id)
    ).pipe(
      mergeMap((result) => {
        if (id === null) {
          return this.store.dispatch(new NodeControlActions.GetRootNodesSuccess());
        }
        return of(result);
      }),
    );
  }

  @Action(NodeControlActions.GetNodesByParentId)
  getNodesByParentId({getState}: StateContext<NodeControlStateModel>,
                     {parentId}: NodeControlActions.GetNodesByParentId) {
    if (parentId === null) {
      if (getState().rootNodesLoaded) {
        return;
      }
    } else {
      const parentNode = this.store.selectSnapshot(NodeEntityState.nodeById$(parentId));
      if (parentNode) {
        const childNodes = this.store.selectSnapshot(NodeEntityState.nodesByParentId$(parentId));
        if (parentNode.children?.length === childNodes.length) {
          return;
        }
      }
    }
    return this.apiControlService.sendMessage(
      new NodeControlActions.GetNodesByParentIdRequest(parentId)
    ).pipe(
      mergeMap((result) => {
        if (parentId === null) {
          return this.store.dispatch(new NodeControlActions.GetRootNodesSuccess())
        }
        return of(result);
      })
    );
  }

  @Action(NodeControlActions.GetNodesByCurrentRoute)
  getNodesByCurrentRoute() {
    const currentSelectedNode = this.store.selectSnapshot(NodeEntityState.currentSelectedNode$);
    if (!currentSelectedNode) {
      const route = this.store.selectSnapshot(NavigationControlState.currentUrl$);
      if (route) {
        return this.apiControlService.sendMessage(
          new NodeControlActions.GetNodesByRouteRequest(route)
        );
      } else {
        return this.store.dispatch(new NodeControlActions.GetNodesByParentId(null));
      }
    } else {
      const node = this.store.selectSnapshot(NodeEntityState.nodeById$(currentSelectedNode.id));
      if (node) {
        return;
      }
      return this.store.dispatch(new NodeControlActions.GetNodesByParentId(currentSelectedNode.id));
    }
  }

  @Action(NodeControlActions.GetRouteById)
  getRouteById(_: StateContext<NodeControlStateModel>,
               {id}: NodeControlActions.GetRouteById) {
    return this.apiControlService.sendMessage(
      new NodeControlActions.GetRouteByIdRequest(id)
    );
  }

  @Action(NodeControlActions.GetRouteByIdSuccess)
  getRouteByIdSuccess(_: StateContext<NodeControlStateModel>,
                      {route}: NodeControlActions.GetRouteByIdSuccess) {
    this.store.dispatch(new Navigate([route], undefined, { replaceUrl: true}))
  }

  //</editor-fold>
}
